import useSWR, { type SWRConfiguration, type SWRResponse } from 'swr';

import { buildSearchParams } from './buildSearchParams';
import { readPlacementTraceId } from './placementTraceId';
import type { Fetcher, SerializableRecord } from './types';

export const REQUEST_URL = `/gateway/api/post-office/api/v1/placements/`;

const defaultHeaders = {
	credentials: 'include',
	mode: 'cors',
	'Content-Type': 'application/json',
};

export const useFetchPlacementData = <
	TData extends SerializableRecord,
	TContext extends SerializableRecord,
>(
	placementId: string | null, // null if disabled
	path: string,
	context: TContext,
	fetcher: Fetcher,
	requestOptions?: RequestInit,
	useSWROptions?: SWRConfiguration,
): SWRResponse<TData, Error> => {
	const normalizedPath =
		path === '' || path === '/' ? '' : `/${path.charAt(0) === '/' ? path.substring(1) : path}`;

	const queryParams = buildSearchParams(context);

	const contextAsSearchParams = queryParams.toString();

	const formattedSearchParams = contextAsSearchParams
		? `?${contextAsSearchParams}`
		: contextAsSearchParams;

	const url = encodeURI(`${REQUEST_URL}${placementId}${normalizedPath}${formattedSearchParams}`);

	return useSWR(
		placementId ? url : null,
		(url) =>
			fetcher
				.fetch(url, {
					...requestOptions,
					headers: {
						...defaultHeaders,
						...requestOptions?.headers,
					},
				})
				?.then(async (res) => {
					if (!res) {
						throw new Error('No response from fetch');
					}

					const body = await res.json();
					const traceId = readPlacementTraceId(res);
					if (!traceId || !body || typeof body !== 'object' || Array.isArray(body)) {
						return body;
					}

					return { ...body, traceId };
				}),
		useSWROptions,
	);
};
